import React, { useCallback, useEffect, useState } from 'react'

import CustomCheckbox from '../CustomCheckbox'

import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'

export interface Interest {
  id: string
  name: string
  included: boolean
}

export default function InterestsEditor({
  data,
  onChange,
  onAddInterestRef,
}: {
  data: Interest[]
  onChange: (data: Interest[]) => void
  onAddInterestRef?: (fn: () => void) => void
}) {
  const interests = Array.isArray(data) ? data : []
  const [editingInterestId, setEditingInterestId] = useState<string | null>(null)
  const [editingInterestName, setEditingInterestName] = useState('')
  const [newInterestInput, setNewInterestInput] = useState<string | null>(null)

  const handleShowAddInterest = useCallback(() => {
    setNewInterestInput('')
  }, [])

  useEffect(() => {
    if (!onAddInterestRef) return
    onAddInterestRef(handleShowAddInterest)
    return () => {
      onAddInterestRef(() => {})
    }
  }, [handleShowAddInterest, onAddInterestRef])
  const handleAddInterest = () => {
    if (newInterestInput.trim()) {
      // Split by comma and create multiple interests
      const interestNames = newInterestInput
        .split(',')
        .map(name => name.trim())
        .filter(name => name.length > 0)
      
      if (interestNames.length > 0) {
        const newInterests = interestNames.map((interestName, index) => ({
          id: `interest-${Date.now()}-${index}-${crypto.randomUUID()}`,
          name: interestName,
          included: true,
        }))
        onChange([...interests, ...newInterests])
        setNewInterestInput(null)
      }
    }
  }

  const handleDeleteInterest = (interestId: string) => {
    onChange(interests.filter((i) => i.id !== interestId))
  }

  const handleToggleInterest = (interestId: string) => {
    const updated = interests.map((interest) =>
      interest.id === interestId ? { ...interest, included: !interest.included } : interest
    )
    onChange(updated)
  }

  const handleStartEditInterest = (interest: Interest) => {
    setEditingInterestId(interest.id)
    setEditingInterestName(interest.name)
  }

  const handleSaveInterest = (interestId: string) => {
    if (editingInterestName.trim()) {
      const updated = interests.map((interest) =>
        interest.id === interestId ? { ...interest, name: editingInterestName.trim() } : interest
      )
      onChange(updated)
    }
    setEditingInterestId(null)
    setEditingInterestName('')
  }

  const renderCheckbox = (checked: boolean, onChange: () => void) => (
    <CustomCheckbox checked={checked} onChange={onChange} size="sm" />
  )

  return (
    <div className="space-y-3">
      {interests.length === 0 && <ResumeSectionEmptyHint sectionType="interests" />}

      {interests.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {interests.map((interest) => {
            return (
              <div
                key={interest.id}
                className={`
                flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-all
                ${interest.included 
                  ? 'bg-surface-default border-interactive text-text-primary' 
                  : 'bg-surface-muted border-border-default text-text-muted'
                }
                hover:shadow-sm
              `}
              >
                <div 
                  role="button"
                  tabIndex={0}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      handleToggleInterest(interest.id)
                    }
                  }}
                >
                  {renderCheckbox(
                    interest.included,
                    () => handleToggleInterest(interest.id)
                  )}
                </div>
                {editingInterestId === interest.id ? (
                  <input
                    type="text"
                    value={editingInterestName}
                    onChange={(e) => setEditingInterestName(e.target.value)}
                    onBlur={() => handleSaveInterest(interest.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleSaveInterest(interest.id)
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        setEditingInterestId(null)
                        setEditingInterestName('')
                      }
                    }}
                    className="px-1 py-0.5 text-sm border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring min-w-[100px]"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />
                ) : (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => handleStartEditInterest(interest)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleStartEditInterest(interest)
                      }
                    }}
                    className="cursor-text text-sm"
                  >
                    {interest.name}
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteInterest(interest.id)
                  }}
                  className="ml-1 p-0.5 text-text-muted hover:text-danger transition-colors"
                  title="Delete interest"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Add Interest Input */}
      {newInterestInput !== null && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newInterestInput ?? ''}
            onChange={(e) => setNewInterestInput(e.target.value)}
            onBlur={() => {
              if (newInterestInput.trim()) {
                handleAddInterest()
              } else {
                setNewInterestInput(null)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddInterest()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setNewInterestInput(null)
              }
            }}
            placeholder="Enter interest names (comma-separated)"
            className="px-2 py-1 text-sm border border-border-default rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring flex-1"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
      )}

      {/* Add another interest when list is non-empty */}
      {interests.length > 0 && newInterestInput === null && (
        <button
          onClick={handleShowAddInterest}
          className="text-sm text-interactive hover:text-interactive font-medium"
        >
          + Add Interest
        </button>
      )}
    </div>
  )
}
